// This is CandleStick.js
import React from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Bar
} from 'recharts';
import { PERIOD_CONFIG } from '../DateFilterButtons';
import './CandleStick.css';

/** Format to YYYY-MM-DD (safe if already ISO). */
const formatISODate = (dateStr) => {
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    return dateStr.slice(0, 10);
  }
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return String(dateStr);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getUTCDate = (dateStr) => {
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? null : d;
};

const getWeeklyBucketStartUTC = (date) => {
  const utcMidnight = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = utcMidnight.getUTCDay();
  const daysFromMonday = (day + 6) % 7;
  utcMidnight.setUTCDate(utcMidnight.getUTCDate() - daysFromMonday);
  return utcMidnight;
};

const getBucketKey = (dateStr, granularity) => {
  const d = getUTCDate(dateStr);
  if (!d) return String(dateStr);

  if (granularity === 'weekly') {
    return getWeeklyBucketStartUTC(d).toISOString().slice(0, 10);
  }
  if (granularity === 'monthly') {
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    return `${y}-${m}-01`;
  }
  if (granularity === 'yearly') {
    return `${d.getUTCFullYear()}-01-01`;
  }
  return d.toISOString().slice(0, 10);
};

const getYearFromDateKey = (dateStr) => {
  if (typeof dateStr === 'string' && /^\d{4}/.test(dateStr)) {
    return Number(dateStr.slice(0, 4));
  }
  const d = getUTCDate(dateStr);
  return d ? d.getUTCFullYear() : null;
};

const aggregateCandles = (data, granularity) => {
  if (granularity === 'daily') return data;

  const sorted = [...data].sort((a, b) => {
    const timeA = new Date(a.date).getTime();
    const timeB = new Date(b.date).getTime();
    return timeA - timeB;
  });

  const buckets = new Map();
  sorted.forEach((item) => {
    const bucketKey = getBucketKey(item.date, granularity);
    const existing = buckets.get(bucketKey);

    if (!existing) {
      buckets.set(bucketKey, {
        ...item,
        date: bucketKey,
        high: item.high,
        low: item.low,
        open: item.open,
        close: item.close,
        volume: Number(item.volume) || 0
      });
      return;
    }

    existing.high = Math.max(existing.high, item.high);
    existing.low = Math.min(existing.low, item.low);
    existing.close = item.close;
    existing.volume += Number(item.volume) || 0;
  });

  return Array.from(buckets.values());
};

const CandleStick = ({ chartData, stockInfo, selectedPeriod }) => {
  if (!chartData || !chartData.length) return <p>Loading chart data...</p>;

  const granularity = PERIOD_CONFIG[selectedPeriod]?.candleGranularity || 'daily';
  const candleData = aggregateCandles(chartData, granularity);

  // Find min and max values for domain calculation
  const highestPrice = Math.max(...candleData.map(item => item.high));
  const lowestPrice = Math.min(...candleData.map(item => item.low));
  const pricePadding = (highestPrice - lowestPrice) * 0.1; // 10% padding

  // Calculate custom ticks that increment by 5
  const minTick = Math.floor((lowestPrice - pricePadding) / 5) * 5;
  const maxTick = Math.ceil((highestPrice + pricePadding) / 5) * 5;
  const customTicks = [];
  for (let i = minTick; i <= maxTick; i += 5) {
    customTicks.push(i);
  }

  // Month vs Year x-axis logic (parity with VWAP/Volume/Change%)
  const showYears = PERIOD_CONFIG[selectedPeriod]?.showYearsOnXAxis || false;
  const xTicks = showYears
    ? Array.from(new Set(candleData.map(d => getYearFromDateKey(d.date)).filter(Boolean)))
        .map(year => candleData.find(d => getYearFromDateKey(d.date) === year)?.date)
        .filter(Boolean)
    : undefined;

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      if (data.isAxisAnchor) return null;
      return (
        <div className="candlestick-tooltip">
          <p className="date"><b>{formatISODate(data.date)}</b></p>
          <p className="open">Open: ${data.open.toFixed(2)}</p>
          <p className="close">Close: ${data.close.toFixed(2)}</p>
          <p className="high">High: ${data.high.toFixed(2)}</p>
          <p className="low">Low: ${data.low.toFixed(2)}</p>
          <p className="volume">Volume: {data.volume.toLocaleString()}</p>
        </div>
      );
    }
    return null;
  };

  const getShiftedDate = (dateStr, days) => {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  };

  const chartDataForCandles =
    selectedPeriod === '1W' && candleData.length > 0
      ? [
          { ...candleData[0], date: getShiftedDate(candleData[0].date, -1), isAxisAnchor: true },
          ...candleData
        ]
      : candleData;

  // Prepare data with additional calculated properties
  const processedData = chartDataForCandles.map(item => {
    const isUp = item.close >= item.open;
    return {
      ...item,
      // For correct rendering in the bar chart
      value: [item.low, item.high], // This creates a proper range for the bar
      isUp
    };
  });

  return (
    <div className="chart-content">
      <h3>Candlestick Chart - {stockInfo.name} ({stockInfo.ticker})</h3>

      <div className="candlestick-chart-container">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={processedData}
            margin={{ top: 12, right: 10, left: 8, bottom: 6 }}
            barCategoryGap="0%"
            barGap={0}
          >
            <CartesianGrid strokeDasharray="3 3" />

            <XAxis
              dataKey="date"
              scale="point"
              padding={{ left: 0, right: 30 }}
              ticks={xTicks}
              tickFormatter={(value) => {
                return showYears
                  ? String(getYearFromDateKey(value) || '')
                  : new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              }}
              tick={{ fontSize: 12 }}
            />

            <YAxis
              domain={[lowestPrice - pricePadding, highestPrice + pricePadding]}
              tickFormatter={(value) => `$${value.toFixed(0)}`}
              ticks={customTicks}
              width={62}
              tick={{ fontSize: 12 }}
            />

            <Tooltip
              content={<CustomTooltip />}
              cursor={{ stroke: '#94a3b8', strokeWidth: 1, fill: 'transparent' }}
            />

            <Bar
              dataKey="value"
              name=""
              isAnimationActive={false}
              legendType="none"
              shape={(props) => {
                if (!props || !props.payload) return null;

                const { x, y, width, height, payload, index } = props;
                const { isUp, open, close, high, low } = payload;
                const color = isUp ? '#22c55e' : '#ef4444';

                // Skip first candle so it does not overlap the Y axis when aligned to ticks.
                if (index === 0) return null;

                // Chart is inverted (y=0 at top), so we need to calculate positions
                // Get the Y coordinate scale
                const yScale = height / (high - low);

                // Calculate all Y positions relative to the bar's position
                // Center each candle on the same x-point used by the date tick/cursor.
                const xCenter = x + width / 2;
                const yHigh = y;
                const yLow = y + height;
                const yOpen = y + (high - open) * yScale;
                const yClose = y + (high - close) * yScale;

                // Calculate body coordinates
                const bodyTop = Math.min(yOpen, yClose);
                const bodyBottom = Math.max(yOpen, yClose);
                const bodyHeight = Math.max(1, bodyBottom - bodyTop); // At least 1px tall
                const bodyWidth = width * 0.6; // 60% of available width

                return (
                  <g>
                    {/* High to Low vertical line (the wick) */}
                    <line
                      x1={xCenter}
                      y1={yHigh}
                      y2={yLow}
                      x2={xCenter}
                      stroke={color}
                      strokeWidth={1}
                      className={isUp ? 'bullish wick' : 'bearish wick'}
                    />

                    {/* Open-Close rectangle (the body) */}
                    <rect
                      x={xCenter - bodyWidth / 2}
                      y={bodyTop}
                      width={bodyWidth}
                      height={bodyHeight}
                      fill={color}
                      className={isUp ? 'bullish body' : 'bearish body'}
                    />
                  </g>
                );
              }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default CandleStick;
